import { useEffect, useState } from "react";
import axios from "axios";
import Button from "../../components/ui/buttons/Button";
import CameraInput from "../../components/ui/buttons/CameraInput";
import FeatureTypeSelect from "../../components/ui/dropdowns/FeatureTypeSelect";
import Form from "../../components/ui/input/Form";
import FullScreenActivityIndicator from "../../components/ui/activityIndicator/FullScreenActivityIndicator";
import ImageInput from "../../components/ui/buttons/ImageInput";
import ImageListCard from "../../components/ui/containers/ImageListCard";
import ImagePreviewPrompt from "../../components/ui/prompts/ImagePreviewPrompt";
import InfoPrompt from "../../components/ui/prompts/InfoPrompt";
import PhotoPointCard from "../../components/ui/containers/PhotoPointCard";
import SectionHeader from "../../components/ui/labels/SectionHeader";
import styles from "./Collect.module.css";
import Switch from "../../components/ui/switch/Switch";
import TextArea from "../../components/ui/input/TextArea";
import useCollect from "./useCollect";
import DevModeLabel from "../../components/ui/labels/DevModeLabel";
import { useNavigate, useLocation } from "react-router-dom";

const Collect = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [selectedPhotoPoint] = useState(routeLocation.state);

  // State and business logic imported from hook.
  const {
    addressResponseErrorHandler,
    addressResponseHandler,
    authCtx,
    clearStatus,
    commentsChangeHandler,
    conditionToggle,
    featureSelectHandler,
    initializeComponent,
    location,
    processImagesHandler,
    removePhotoHandler,
    setPreview,
    state,
    submitCreateEditHandler,
  } = useCollect({
    selectedPhotoPoint: selectedPhotoPoint,
  });

  const cancelEditHandler = () => {
    navigate("/home", {
      replace: true,
      state: {
        latitude: selectedPhotoPoint.LOCATION.latitude,
        longitude: selectedPhotoPoint.LOCATION.longitude,
      },
    });
  };

  // On component load, attempt to update the user's location that was
  // freshly fetched from the useCollect hook.
  useEffect(() => {
    if (location?.latitude && location?.longitude) {
      selectedPhotoPoint.USER_LATITUDE = location.latitude;
      selectedPhotoPoint.USER_LONGITUDE = location.longitude;
    }
    // eslint-disable-next-line
  }, []);

  // Initialize data and fetch the nearest built address.
  useEffect(() => {
    // Clean up async useEffect flag.
    let isSubscribed = true;
    initializeComponent();
    let url = `${process.env.REACT_APP_API_URL}/data/address`;
    let authObj = { headers: { Authorization: `token ${authCtx.token}` } };
    axios
      .post(url, selectedPhotoPoint.LOCATION, authObj)
      .then((response) => {
        if (isSubscribed) {
          addressResponseHandler(response);
        }
      })
      .catch((error) => {
        if (isSubscribed) {
          addressResponseErrorHandler(error);
        }
      });
    // Cancel subscription to useEffect.
    return () => (isSubscribed = false);
    //eslint-disable-next-line
  }, [selectedPhotoPoint, authCtx.token]);

  // JSX.
  return (
    <>
      <SectionHeader text="Collect" overrideFontSize={"24px"} />
      <DevModeLabel />
      <PhotoPointCard selectedPhotoPoint={selectedPhotoPoint} />
      <Form noValidate={true}>
        <Switch
          label={state.condition ? `Condition: Good` : `Condition: Bad`}
          identifier={"condition"}
          isChecked={state.condition}
          setIsChecked={conditionToggle}
        />
        <FeatureTypeSelect
          sdeTable={state.sdeTable}
          identifier="selectedFeature"
          value={state.selectedFeature}
          selectHandler={featureSelectHandler}
          required={true}
        />
        <TextArea
          label={"Comments"}
          placeholder={"Comments"}
          identifier={"comments"}
          onChange={commentsChangeHandler}
          value={state.comments}
        />
        <div className={styles.imageInputDiv}>
          <CameraInput
            id="collectCameraInput"
            imageHandler={processImagesHandler}
            disabled={state.selectedFeature.value === ""}
          />
          <ImageInput
            id="collectImageInput"
            imageHandler={processImagesHandler}
            disabled={state.selectedFeature.value === ""}
          />
        </div>
      </Form>
      {state.imageObjects.length > 0 && (
        <ImageListCard
          photos={state.imageObjects}
          setPreview={setPreview}
          deleteAllowed={true}
          removePhotoHandler={removePhotoHandler}
        />
      )}
      {!selectedPhotoPoint.HAS_PHOTO_POINT &&
        state.imageObjects.length === 0 && (
          <SectionHeader text="Upload Photo(s)" />
        )}

      <ImagePreviewPrompt
        preview={state.preview}
        confirmHandler={() => setPreview(null)}
        setConfirmDeletePrompt={() => removePhotoHandler(state.preview)}
      />
      <div className={styles.primaryButtons}>
        <Button
          disabled={
            state.loading ||
            (!selectedPhotoPoint.HAS_PHOTO_POINT && !state.formValid)
          }
          onClick={submitCreateEditHandler}
          text="Submit"
        />
        <Button onClick={cancelEditHandler} text="Cancel" />
      </div>
      <FullScreenActivityIndicator
        loading={state.loading}
        text="Submitting Request..."
      />
      <FullScreenActivityIndicator
        loading={state.processingImage}
        text="Processing Image(s)..."
      />
      <InfoPrompt text={state.status} statusCleared={clearStatus} />
      <InfoPrompt
        text={state.submissionStatus}
        statusCleared={cancelEditHandler}
      />
    </>
  );
};

export default Collect;
